// src/pages/company-head/OrderManagement.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import { 
  FaShoppingCart, FaSearch, FaFilter, FaCalendar, FaUser, FaBox,
  FaClock, FaMoneyBillWave, FaCheck, FaTimes, FaEye, FaEdit,
  FaTruck, FaCheckCircle, FaTimesCircle, FaSpinner
} from 'react-icons/fa';

const OrderManagement = () => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalValue: 0
  });
  
  // Filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalOrders: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Get company ID
  const getCompanyId = () => {
    if (typeof user?.user?.company === 'string') {
      return user.user.company;
    }
    return user?.user?.company?._id || user?.user?.company?.id;
  };

  // Fetch orders
  const fetchOrders = async (page = 1, search = '', status = '', date = '') => {
    try {
      setLoading(true);
      const companyId = getCompanyId();
      
      if (!companyId) {
        console.error('No company ID found');
        return;
      }

      // Placeholder data since API endpoints would need to be created
      setTimeout(() => {
        setOrders([
          {
            _id: '1',
            orderId: 'ORD-2025-001',
            dealerId: 'AD00000001',
            dealerName: 'RAJESH TILES MART',
            items: [
              { name: 'Premium Ceramic Tiles 60x60', quantity: 100, price: 850, total: 85000 },
              { name: 'Marble Finish Tiles', quantity: 50, price: 1200, total: 60000 }
            ],
            totalAmount: 145000,
            status: 'pending',
            orderDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            expectedDelivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
            notes: 'Urgent order for ongoing project'
          },
          {
            _id: '2',
            orderId: 'ORD-2025-002',
            dealerId: 'AD00000002',
            dealerName: 'CERAMIC WORLD',
            items: [
              { name: 'Designer Wall Tiles', quantity: 75, price: 950, total: 71250 }
            ],
            totalAmount: 71250,
            status: 'processing',
            orderDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            expectedDelivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
            notes: 'Custom size requirement'
          },
          {
            _id: '3',
            orderId: 'ORD-2025-003',
            dealerId: 'AD00000003',
            dealerName: 'TILE PARADISE',
            items: [
              { name: 'Anti-Skid Floor Tiles', quantity: 200, price: 650, total: 130000 },
              { name: 'Natural Stone Tiles', quantity: 30, price: 1500, total: 45000 }
            ],
            totalAmount: 175000,
            status: 'shipped',
            orderDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            expectedDelivery: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
            notes: 'Bulk order with discount applied'
          },
          {
            _id: '4',
            orderId: 'ORD-2025-004',
            dealerId: 'AD00000001',
            dealerName: 'RAJESH TILES MART',
            items: [
              { name: 'Vitrified Tiles', quantity: 120, price: 750, total: 90000 }
            ],
            totalAmount: 90000,
            status: 'delivered',
            orderDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            expectedDelivery: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            notes: 'Delivered on time'
          },
          {
            _id: '5',
            orderId: 'ORD-2025-005',
            dealerId: 'AD00000002',
            dealerName: 'CERAMIC WORLD',
            items: [
              { name: 'Bathroom Tiles', quantity: 80, price: 1100, total: 88000 }
            ],
            totalAmount: 88000,
            status: 'cancelled',
            orderDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            expectedDelivery: null,
            notes: 'Cancelled due to size mismatch'
          }
        ]);
        
        setStats({
          totalOrders: 156,
          pendingOrders: 23,
          completedOrders: 98,
          totalValue: 2850000
        });
        
        setPagination({
          totalPages: 1,
          totalOrders: 5,
          hasNextPage: false,
          hasPrevPage: false
        });
        
        setLoading(false);
      }, 1000);

    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Placeholder for actual API call
      setOrders(orders.map(order => 
        order._id === orderId 
          ? { ...order, status: newStatus }
          : order
      ));
      
      alert(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating order status');
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders(1, searchTerm, statusFilter, dateFilter);
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    fetchOrders(1, searchTerm, status, dateFilter);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending': return 'bg-yellow-100 text-yellow-800';
        case 'processing': return 'bg-blue-100 text-blue-800';
        case 'shipped': return 'bg-purple-100 text-purple-800';
        case 'delivered': return 'bg-green-100 text-green-800';
        case 'cancelled': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'pending': return <FaClock className="mr-1" />;
        case 'processing': return <FaSpinner className="mr-1" />;
        case 'shipped': return <FaTruck className="mr-1" />;
        case 'delivered': return <FaCheckCircle className="mr-1" />;
        case 'cancelled': return <FaTimesCircle className="mr-1" />;
        default: return null;
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600 mb-4" role="status"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Management</h1>
        <p className="text-gray-600">Manage dealer orders and track delivery status</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
            <FaShoppingCart className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
            </div>
            <FaClock className="text-yellow-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedOrders}</p>
            </div>
            <FaCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(stats.totalValue)}</p>
            </div>
            <FaMoneyBillWave className="text-purple-500 text-2xl" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID, dealer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>

          {/* Date Filter */}
          <div className="relative">
            <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleStatusFilter('')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                statusFilter === '' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleStatusFilter('pending')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                statusFilter === 'pending' 
                  ? 'bg-yellow-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => handleStatusFilter('processing')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                statusFilter === 'processing' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Processing
            </button>
            <button
              onClick={() => handleStatusFilter('delivered')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                statusFilter === 'delivered' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Delivered
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dealer Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items & Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.orderId}</div>
                    <div className="text-sm text-gray-500">{order.notes}</div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                          {order.dealerName.charAt(0)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{order.dealerName}</div>
                        <div className="text-sm text-gray-500">ID: {order.dealerId}</div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="font-medium">{order.items.length} item(s)</div>
                      <div className="text-lg font-bold text-green-600">{formatCurrency(order.totalAmount)}</div>
                      <div className="text-xs text-gray-500">
                        {order.items.slice(0, 2).map((item, index) => (
                          <div key={index}>{item.name} ({item.quantity})</div>
                        ))}
                        {order.items.length > 2 && <div>+{order.items.length - 2} more...</div>}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={order.status} />
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center mb-1">
                      <FaCalendar className="mr-2 text-gray-400" />
                      {formatDate(order.orderDate)}
                    </div>
                    {order.expectedDelivery && (
                      <div className="flex items-center">
                        <FaTruck className="mr-2 text-gray-400" />
                        {formatDate(order.expectedDelivery)}
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      
                      {order.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateOrderStatus(order._id, 'processing')}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Approve Order"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => updateOrderStatus(order._id, 'cancelled')}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Cancel Order"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                      
                      {order.status === 'processing' && (
                        <button
                          onClick={() => updateOrderStatus(order._id, 'shipped')}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded"
                          title="Mark as Shipped"
                        >
                          <FaTruck />
                        </button>
                      )}
                      
                      {order.status === 'shipped' && (
                        <button
                          onClick={() => updateOrderStatus(order._id, 'delivered')}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Mark as Delivered"
                        >
                          <FaCheckCircle />
                        </button>
                      )}
                      
                      <button
                        className="text-gray-600 hover:text-gray-900 p-1 rounded"
                        title="Edit Order"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && (
          <div className="text-center py-12">
            <FaShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Orders from dealers will appear here once they start placing orders through the mobile app.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
